import React, { useState, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Spinner } from "../ui/spinner";
import { Edit2, Trash2 } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "../../api/apiClient";
import { API_ENDPOINTS } from "../../api/header";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import { format } from "date-fns";

const CustomerNotesModal = ({ open, onOpenChange, customerId }) => {
  const queryClient = useQueryClient();

  const rawPermissions = useSelector((state) => state.userpermission_data);
  const permissions = useMemo(() => rawPermissions || [], [rawPermissions]);
  const hasPermission = useCallback(
    (permission) => permissions.includes(permission),
    [permissions]
  );

  const [editingNoteId, setEditingNoteId] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [source, setSource] = useState("");
  const [notedAt, setNotedAt] = useState(() => format(new Date(), "yyyy-MM-dd'T'HH:mm"));
  const [errors, setErrors] = useState({});

  const resetForm = useCallback(() => {
    setEditingNoteId(null);
    setNoteText("");
    setSource("");
    setNotedAt(format(new Date(), "yyyy-MM-dd'T'HH:mm"));
    setErrors({});
  }, []);

  const { data: notesData = [], isLoading } = useQuery({
    queryKey: ["customer-notes", customerId],
    queryFn: async () => {
      if (!customerId) return [];
      const res = await apiClient.get(API_ENDPOINTS.SALES.CUSTOMER_NOTES, {
        params: { customer_id: customerId },
      });
      return res.data.data || [];
    },
    enabled: !!customerId && open,
  });

  const saveNoteMutation = useMutation({
    mutationFn: async (data) => {
      if (editingNoteId) {
        return await apiClient.put(`${API_ENDPOINTS.SALES.CUSTOMER_NOTES}/${editingNoteId}`, data);
      }
      return await apiClient.post(API_ENDPOINTS.SALES.CUSTOMER_NOTES, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-notes", customerId] });
      toast.success(editingNoteId ? "Note updated successfully!" : "Note added successfully!");
      handleCancelEdit();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || `Failed to ${editingNoteId ? 'update' : 'add'} note!`);
    },
  });

  const deleteNoteMutation = useMutation({
    mutationFn: async (id) => {
      return await apiClient.delete(`${API_ENDPOINTS.SALES.CUSTOMER_NOTES}/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-notes", customerId] });
      toast.success("Note deleted successfully!");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to delete note!");
    },
  });

  const validateForm = () => {
    const newErrors = {};
    if (!noteText.trim()) {
      newErrors.noteText = "Note is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;

    saveNoteMutation.mutate({
      customer_id: customerId,
      note: noteText,
      source: source || null,
      noted_at: notedAt ? new Date(notedAt).toISOString() : null,
    });
  };

  const handleEditClick = (noteItem) => {
    setEditingNoteId(noteItem.id);
    setNoteText(noteItem.note);
    setSource(noteItem.source || "");
    setNotedAt(
      noteItem.noted_at
        ? format(new Date(noteItem.noted_at), "yyyy-MM-dd'T'HH:mm")
        : format(new Date(), "yyyy-MM-dd'T'HH:mm")
    );
    setErrors({});
  };

  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setNoteText("");
    setSource("");
    setNotedAt(format(new Date(), "yyyy-MM-dd'T'HH:mm"));
    setErrors({});
  };

  const showForm =
    (editingNoteId && hasPermission("customer note update")) ||
    (!editingNoteId && hasPermission("customer note create"));

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (isOpen) resetForm(); onOpenChange(isOpen); }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-5 pt-5 pb-3 border-b">
          <DialogTitle>Customer Notes</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {/* Notes List */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium">Existing Notes</h4>
            {isLoading ? (
              <div className="flex justify-center p-4">
                <Spinner size={24} />
              </div>
            ) : notesData.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">No notes found for this customer.</p>
            ) : (
              <div className="space-y-3">
                {notesData.map((noteItem, idx) => (
                  <div key={noteItem.id || idx} className={`bg-muted/30 p-3 rounded-md border ${editingNoteId === noteItem.id ? 'border-primary ring-1 ring-primary/20' : ''}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground font-medium">
                          {noteItem.noted_at ? format(new Date(noteItem.noted_at), "PPP p") : "Unknown Date"}
                        </span>
                        {noteItem.source && (
                          <span className="text-xs bg-muted px-2 py-0.5 rounded border">Source: {noteItem.source}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {hasPermission("customer note update") && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => handleEditClick(noteItem)}
                          >
                            <Edit2 className="w-4 h-4 text-blue-600" />
                          </Button>
                        )}
                        {hasPermission("customer note delete") && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => {
                              if (window.confirm("Are you sure you want to delete this note?")) {
                                deleteNoteMutation.mutate(noteItem.id);
                              }
                            }}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{noteItem.note}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {showForm && (
            <>
              <hr />

              {/* Add/Edit Note Form */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium">
                    {editingNoteId ? "Edit Note" : "Add New Note"}
                  </h4>
                  {editingNoteId && (
                    <Button variant="ghost" size="sm" onClick={handleCancelEdit}>
                      Cancel Edit
                    </Button>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Note <span className="text-destructive">*</span></Label>
                  <Textarea
                    placeholder="Enter note details here..."
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    className={`min-h-[100px] ${errors.noteText ? "border-destructive" : ""}`}
                  />
                  {errors.noteText && (
                    <p className="text-destructive text-sm">{errors.noteText}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Source</Label>
                    <Input
                      placeholder="e.g. Phone call, Email"
                      value={source}
                      onChange={(e) => setSource(e.target.value)}
                      maxLength={255}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Noted At</Label>
                    <Input
                      type="datetime-local"
                      value={notedAt}
                      onChange={(e) => setNotedAt(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="px-5 py-4 border-t mt-auto">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {showForm && (
            <Button onClick={handleSave} disabled={saveNoteMutation.isPending}>
              {saveNoteMutation.isPending ? (
                <>
                  <Spinner size={16} className="mr-2" />
                  Saving...
                </>
              ) : editingNoteId ? (
                "Update Note"
              ) : (
                "Add Note"
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerNotesModal;
